import React, {Fragment, useRef, useState} from "react";
import {ArrowDropDown, Settings} from "@mui/icons-material";
import {IconButton, ListSubheader, Menu, MenuItem, Tooltip} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Input from "@mui/material/Input";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import DashboardsManager from "../dashboards-manager";
import ModuleMenuItemPlaceholder from "../module-menu-item-placeholder";
import ModulesPlaceholder from "../modules-placeholder";
import SettingsDialog from "../settings-dialog";
import {downloadQueryLog} from "./actions";
import AppSettingsForm from "./app-settings-form";
import FilterSettingsForm from "./dashboard-filters-settings";
import LicenseForm from "./license-form";
import MachineToMachineSettingsForm from "./machine-to-machine-settings-form";
import NodesSettingsForm from "./nodes-settings";
import UserItemContainersSettingsForm from "./user-itemContainers-settings-form";
import ItemContainerSettingsFormProvider from "./user-itemContainers-settings-form/ItemContainerSettingsFormProvider";
import UsersSettingsForm from "./users-settings";
import {
  addHubConfigDashboardsDashboard,
  clearAllHubConfigDashboards,
  clearHubConfigDashboards,
  fetchAllHubConfigDashboards,
  fetchHubConfigDashboards,
  removeHubConfigDashboardsDashboard,
  sendHubConfigDashboardsOrders,
  setHubConfigMachineToMachinePassword,
  submithHubConfigDashboards
} from "../../state/hub-config/hubConfigActions";
import {
  canDisplayAppSettingsForm,
  canDisplayNodesSettingsForm,
  canDisplayUsersSettingsForm,
  canGetQueryLog,
  canManageAppDashboards,
  canManageFilters,
  canManagePersonalCustomPages,
  canSetMachineToMachinePassword,
  getIsUserSuperadmin
} from "../../utils/permissions";
import {HEADER_BUTTON_VIEW_MODE_ICON, HEADER_BUTTON_VIEW_MODE_ICONTEXT} from "../header/utils";

const SettingsSelect = ({nodes, viewMode, onNavigate}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  // Redux state
  const moduleConfigs = useSelector(state => state.moduleConfigs);
  const user = useSelector(state => state.user);
  const hub = useSelector(state => state.hub);
  const dashboards = useSelector(state => state.hubConfig.hubDashboards);
  const allDashboards = useSelector(state => state.hubConfig.allDashboards);

  // State
  const [anchorEl, setAnchorEl] = useState(null);
  const [isAppOpen, setIsAppOpen] = useState(false);
  const [isHubDashboardManagerOpen, setIsHubDashboardManagerOpen] = useState(false);
  const [isNodesOpen, setIsNodesOpen] = useState(false);
  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [isCustomPageOpen, setIsCustomPageOpen] = useState(false);
  const [isFilterSettingsOpen, setIsFilterSettingsOpen] = useState(false);
  const [isLogDownloadOpen, setIsLogDownloadOpen] = useState(false);
  const [logLimit, setLogLimit] = useState(5);
  const [isMachineToMachinePwdOpen, setIsMachineToMachinePwdOpen] = useState(false);
  const [isLicenseOpen, setIsLicenseOpen] = useState(false);

  // Refs
  const appSettingsRef = useRef(null);
  const dashboardsManagerRef = useRef(null);
  const nodesSettingsRef = useRef(null);
  const usersSettingsRef = useRef(null);
  const customPagesSettingsRef = useRef(null);
  const machineToMachinePwdSettingsRef = useRef(null);

  // Action creators
  const onLogDownload = limit => dispatch(downloadQueryLog(limit));
  // const onCacheClear = () => dispatch(clearMemoryCache());
  const submitDashboards = data => dispatch(submithHubConfigDashboards(data));
  const fetchDashboards = () => dispatch(fetchHubConfigDashboards());
  const fetchAllDashboards = () => dispatch(fetchAllHubConfigDashboards());
  const clearDashboards = () => dispatch(clearHubConfigDashboards());
  const clearAllDashboards = () => dispatch(clearAllHubConfigDashboards());
  const addDashboard = dashboardId => dispatch(addHubConfigDashboardsDashboard(dashboardId));
  const removeDashboard = dashboardId => dispatch(removeHubConfigDashboardsDashboard(dashboardId));
  const sendDashboardsOrders = orderedDashboardIds => dispatch(sendHubConfigDashboardsOrders(orderedDashboardIds));
  const onMachineToMachinePwdSet = (hubId, password) => dispatch(setHubConfigMachineToMachinePassword(hubId, password));

  // Event handlers
  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onAppOpen = () => setIsAppOpen(true);
  const onAppClose = () => setIsAppOpen(false);

  const onHubDashboardManagerOpen = () => setIsHubDashboardManagerOpen(true);
  const onHubDashboardManagerClose = () => setIsHubDashboardManagerOpen(false);

  const onNodesOpen = () => setIsNodesOpen(true);
  const onNodesClose = () => setIsNodesOpen(false);

  const onUsersOpen = () => setIsUsersOpen(true);
  const onUsersClose = () => setIsUsersOpen(false);

  const onCustomPageOpen = () => setIsCustomPageOpen(true);
  const onCustomPageClose = () => setIsCustomPageOpen(false);

  const onFiltersSettingsOpen = () => setIsFilterSettingsOpen(true);
  const onFiltersSettingsClosed = () => setIsFilterSettingsOpen(false);

  const onLogDownloadOpen = () => setIsLogDownloadOpen(true);
  const onLogDownloadClose = () => {
    setIsLogDownloadOpen(false);
    setLogLimit(5);
  };

  const onLogLimitSet = value => {
    if (!isNaN(value)) {
      setLogLimit(value);
    }
  };

  const onMachineToMachinePwdOpen = () => setIsMachineToMachinePwdOpen(true);
  const onMachineToMachinePwdClose = () => setIsMachineToMachinePwdOpen(false);

  const onLicenseOpen = () => setIsLicenseOpen(true);
  const onLicenseClose = () => setIsLicenseOpen(false);

  return (
    <Fragment>
      <Tooltip title={t("components.settingsSelect.title")}>
        {viewMode === HEADER_BUTTON_VIEW_MODE_ICON ? (
          <IconButton onClick={handleOpen} aria-label={t("components.settingsSelect.ariaLabel")} color="inherit">
            <Settings />
          </IconButton>
        ) : (
          <Button
            onClick={handleOpen}
            startIcon={viewMode === HEADER_BUTTON_VIEW_MODE_ICONTEXT ? <Settings /> : null}
            endIcon={<ArrowDropDown />}
            aria-label={t("components.settingsSelect.ariaLabel")}
            classes={{
              endIcon: "button-select__menu-button__end-icon"
            }}
            sx={{
              background: Boolean(anchorEl) ? "#f5f5f5" : undefined
            }}
          >
            {t("components.settingsSelect.label")}
          </Button>
        )}
      </Tooltip>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={handleClose}
        sx={{
          "& .MuiMenuItem-root": {
            paddingLeft: "32px"
          }
        }}
        keepMounted
      >
        {(canDisplayAppSettingsForm(user) ||
          canDisplayNodesSettingsForm(user) ||
          canDisplayUsersSettingsForm(user)) && (
          <ListSubheader>{t("components.settingsSelect.menuSections.admin")}</ListSubheader>
        )}
        {canDisplayAppSettingsForm(user) ? (
          <MenuItem
            key="app"
            id="settings-select__app-btn"
            onClick={() => {
              onAppOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.app")}
          </MenuItem>
        ) : null}
        {canDisplayNodesSettingsForm(user) ? (
          <MenuItem
            key="nodes"
            id="settings-select__nodes-btn"
            onClick={() => {
              onNodesOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.nodes")}
          </MenuItem>
        ) : null}
        {canDisplayUsersSettingsForm(user) ? (
          <MenuItem
            key="user"
            id="settings-select__users-btn"
            onClick={() => {
              onUsersOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.users")}
          </MenuItem>
        ) : null}
        {((moduleConfigs.modules.includes("custom-page") && canManagePersonalCustomPages(user)) ||
          (moduleConfigs.placeholders?.["settings-select-menu"] || []).length > 0) && (
          <ListSubheader>{t("components.settingsSelect.menuSections.portal")}</ListSubheader>
        )}
        {moduleConfigs.modules.includes("custom-page") && canManagePersonalCustomPages(user) ? (
          <MenuItem
            key="customPage"
            id="settings-select__custom-pages-btn"
            onClick={() => {
              onCustomPageOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.customPages")}
          </MenuItem>
        ) : null}
        {...(moduleConfigs.placeholders?.["settings-select-menu"] || []).map(({id, component, fallback}, idx) => (
          <ModuleMenuItemPlaceholder
            key={`module-${idx}`}
            id={id}
            component={component}
            fallback={fallback}
            closeMenu={handleClose}
          />
        ))}
        {((moduleConfigs.modules.includes("dashboard") && canManageAppDashboards(user)) ||
          (moduleConfigs.modules.includes("dashboard") && canManageFilters(user))) && (
          <ListSubheader>{t("components.settingsSelect.menuSections.dashboard")}</ListSubheader>
        )}
        {moduleConfigs.modules.includes("dashboard") && canManageAppDashboards(user) ? (
          <MenuItem
            key="hubDashboardManager"
            id="settings-select__hub-dashboard-manager-btn"
            onClick={() => {
              onHubDashboardManagerOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.hubDashboardManager")}
          </MenuItem>
        ) : null}
        {moduleConfigs.modules.includes("dashboard") && canManageFilters(user) ? (
          <MenuItem
            key="filters"
            id="settings-select__dashboard-filters-btn"
            onClick={() => {
              onFiltersSettingsOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.dashboardFilters")}
          </MenuItem>
        ) : null}
        {(canSetMachineToMachinePassword(user) || canGetQueryLog(user) || getIsUserSuperadmin(user)) && (
          <ListSubheader>{t("components.settingsSelect.menuSections.system")}</ListSubheader>
        )}
        {canSetMachineToMachinePassword(user) ? (
          <MenuItem
            key="macchineToMachinePassword"
            id="settings-select__macchine-to-machine-psw-btn"
            onClick={() => {
              onMachineToMachinePwdOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.macchineToMachinePassword")}
          </MenuItem>
        ) : null}
        {canGetQueryLog(user) ? (
          <MenuItem
            key="queriesLog"
            id="settings-select__queries-log-btn"
            onClick={() => {
              onLogDownloadOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.queriesLog")}
          </MenuItem>
        ) : null}
        {getIsUserSuperadmin(user) ? (
          <MenuItem
            key="license"
            id="settings-select__license-btn"
            onClick={() => {
              onLicenseOpen();
              handleClose();
            }}
          >
            {t("components.settingsSelect.menuItems.license")}
          </MenuItem>
        ) : null}
      </Menu>

      <SettingsDialog
        title={t("components.settingsSelect.dialog.app.title")}
        open={isAppOpen}
        onClose={() => {
          if (appSettingsRef.current) {
            appSettingsRef.current.cancel(() => onAppClose());
          } else {
            onAppClose();
          }
        }}
        onSubmit={() => {
          if (appSettingsRef.current) {
            appSettingsRef.current.submit(() => {
              onAppClose();
            });
          } else {
            onAppClose();
          }
        }}
      >
        <AppSettingsForm ref={appSettingsRef} />
      </SettingsDialog>

      <SettingsDialog
        id={"settings-select__nodes-settings-dialog"}
        title={t("components.settingsSelect.dialog.nodes.title")}
        open={isNodesOpen}
        onClose={() => {
          if (nodesSettingsRef.current) {
            nodesSettingsRef.current.cancel(() => onNodesClose());
          } else {
            onNodesClose();
          }
        }}
      >
        <NodesSettingsForm
          ref={nodesSettingsRef}
          nodes={nodes}
          onNavigate={() => {
            if (nodesSettingsRef.current) {
              nodesSettingsRef.current.cancel(() => {
                onNodesClose();
                if (onNavigate) onNavigate();
              });
            } else {
              onNodesClose();
              if (onNavigate) onNavigate();
            }
          }}
        />
      </SettingsDialog>

      <SettingsDialog
        title={t("components.settingsSelect.dialog.users.title")}
        open={isUsersOpen}
        onClose={() => {
          if (usersSettingsRef.current) {
            usersSettingsRef.current.cancel(() => onUsersClose());
          } else {
            onUsersClose();
          }
        }}
      >
        <UsersSettingsForm ref={usersSettingsRef} />
      </SettingsDialog>

      <SettingsDialog
        title={t("components.settingsSelect.dialog.hubDashboardManager.title")}
        open={isHubDashboardManagerOpen}
        onClose={() => {
          if (dashboardsManagerRef.current) {
            dashboardsManagerRef.current.destroy(() => onHubDashboardManagerClose());
          } else {
            onHubDashboardManagerClose();
          }
        }}
        onSubmit={() => {
          if (dashboardsManagerRef.current) {
            dashboardsManagerRef.current.submit(data => {
              submitDashboards(data);
              onHubDashboardManagerClose();
            });
          } else {
            onHubDashboardManagerClose();
          }
        }}
      >
        <DashboardsManager
          dashboards={dashboards}
          allDashboards={allDashboards}
          fetchDashboards={fetchDashboards}
          fetchAllDashboards={fetchAllDashboards}
          clearDashboards={clearDashboards}
          clearAllDashboards={clearAllDashboards}
          addDashboard={addDashboard}
          removeDashboard={removeDashboard}
          sendDashboardsOrders={sendDashboardsOrders}
          ref={dashboardsManagerRef}
        />
      </SettingsDialog>

      <SettingsDialog
        title={t("components.settingsSelect.dialog.customPages.title")}
        open={isCustomPageOpen}
        onClose={() => {
          if (customPagesSettingsRef.current) {
            customPagesSettingsRef.current.cancel(() => {
              onCustomPageClose();
            });
          } else {
            onCustomPageClose();
          }
        }}
      >
        <ItemContainerSettingsFormProvider>
          <UserItemContainersSettingsForm asDashboard={false} ref={customPagesSettingsRef} />
        </ItemContainerSettingsFormProvider>
      </SettingsDialog>

      <SettingsDialog
        title={t("components.settingsSelect.dialog.dashboardFilters.title")}
        open={isFilterSettingsOpen}
        onClose={() => onFiltersSettingsClosed()}
      >
        <FilterSettingsForm />
      </SettingsDialog>

      <CustomDialog id="settings-select__log-download__dialog" open={isLogDownloadOpen} onClose={onLogDownloadClose}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <div>{t("components.settingsSelect.dialog.queriesLog.content")}:</div>
            </Grid>
            <Grid size={12}>
              <Input
                id="settings-select__log-download__limit-input"
                value={logLimit}
                onChange={ev => onLogLimitSet(ev.target.value)}
                style={{width: "100%"}}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button id="settings-select__log-download__cancel-button" onClick={onLogDownloadClose}>
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            id="settings-select__log-download__confirm-button"
            autoFocus
            onClick={() => {
              onLogDownloadClose();
              onLogDownload(logLimit);
            }}
            color="primary"
          >
            {t("commons.confirm.download")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <CustomDialog
        open={isMachineToMachinePwdOpen}
        onClose={() => {
          if (machineToMachinePwdSettingsRef.current) {
            machineToMachinePwdSettingsRef.current.cancel(() => {
              onMachineToMachinePwdClose();
            });
          } else {
            onMachineToMachinePwdClose();
          }
        }}
      >
        <CustomDialogTitle
          onClose={() => {
            if (machineToMachinePwdSettingsRef.current) {
              machineToMachinePwdSettingsRef.current.cancel(() => {
                onMachineToMachinePwdClose();
              });
            } else {
              onMachineToMachinePwdClose();
            }
          }}
        >
          {t("components.settingsSelect.dialog.machineToMachineSettings.title")}
        </CustomDialogTitle>
        <DialogContent>
          <MachineToMachineSettingsForm ref={machineToMachinePwdSettingsRef} hub={hub.hub} />
        </DialogContent>
        <DialogActions>
          <Button
            className="components__dialog__btn components__dialog__btn--cancel"
            onClick={() => {
              if (machineToMachinePwdSettingsRef.current) {
                machineToMachinePwdSettingsRef.current.cancel(() => {
                  onMachineToMachinePwdClose();
                });
              } else {
                onMachineToMachinePwdClose();
              }
            }}
          >
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            className="components__dialog__btn components__dialog__btn--submit"
            onClick={() => {
              if (machineToMachinePwdSettingsRef.current) {
                machineToMachinePwdSettingsRef.current.submit(({password}) => {
                  onMachineToMachinePwdSet(hub.hub.hubId, password);
                  onMachineToMachinePwdClose();
                });
              } else {
                onMachineToMachinePwdClose();
              }
            }}
          >
            {t("commons.confirm.submit")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <CustomDialog open={isLicenseOpen} onClose={onLicenseClose}>
        <CustomDialogTitle onClose={onLicenseClose}>
          {t("components.settingsSelect.dialog.license.title")}
        </CustomDialogTitle>
        <DialogContent>
          <LicenseForm />
        </DialogContent>
        <DialogActions>
          <Button className="components__dialog__btn components__dialog__btn--close" onClick={onLicenseClose}>
            {t("commons.confirm.close")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <ModulesPlaceholder id="settings-select-dialog" />
    </Fragment>
  );
};

export default SettingsSelect;
